"use client"

import { getLocalizedField } from '@/utils/language-utils';
import { defaultBooleanOptions } from './constants';
import { useLanguage } from '@/context/language-context';
import { useTranslation } from '@/hooks/use-translation';
import { CustomDropdownField } from '@/components/custom-dropdown-field';

interface BooleanCellProps {
  value: boolean | null
  rowId: string
  onSave: (value: boolean) => void
  isEditable?: boolean
  options?: typeof defaultBooleanOptions
}

interface BooleanCellEditorProps {
  value: boolean | null
  onChange: (value: boolean) => void
  options?: typeof defaultBooleanOptions
}

export function BooleanCell({ value, rowId, onSave, isEditable = true, options = defaultBooleanOptions }: BooleanCellProps) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  let displayText = '';
  
  if (value === true) {
    displayText = getLocalizedField(options.find((opt: { code: string }) => opt.code === 'true') || {}, 'title', language);
  } else if (value === false) {
    displayText = getLocalizedField(options.find((opt: { code: string }) => opt.code === 'false') || {}, 'title', language);
  }

  const handleValueChange = (newValue: boolean) => {
    onSave(newValue);
  };

  // If not editable, just show as text
  if (!isEditable) {
    return (
      <span className="text-gray-700">
        {displayText}
      </span>
    );
  }

  // Editable: use dropdown selector (single-select)
  const dropdownOptions = options.map(opt => ({
    value: opt.code,
    label: getLocalizedField(opt as any, 'title', language)
  }));
  const selectedValues = value === true ? ['true'] : value === false ? ['false'] : [];
  return (
    <CustomDropdownField
      options={dropdownOptions}
      selectedValues={selectedValues}
      onChange={(values) => {
        const selected = values[0];
        if (selected === 'true') handleValueChange(true);
        else if (selected === 'false') handleValueChange(false);
      }}
      placeholder={t('selectOption')}
      useColorCode={false}
      multiSelect={false}
      showSearch={false}
      className="z-[100]"
    />
  );
}

export function BooleanCellEditor({ value, options = defaultBooleanOptions, onChange }: BooleanCellEditorProps) {
  const { language } = useLanguage();
  const dropdownOptions = options.map(opt => ({
    value: opt.code,
    label: getLocalizedField(opt as any, 'title', language)
  }));
  const selectedValues = value === true ? ['true'] : value === false ? ['false'] : [];
  return (
    <CustomDropdownField
      options={dropdownOptions}
      selectedValues={selectedValues}
      onChange={(values) => {
        const selected = values[0];
        if (selected === 'true') onChange(true);
        else if (selected === 'false') onChange(false);
      }}
      placeholder={''}
      useColorCode={false}
      multiSelect={false}
      showSearch={false}
      className="z-[100]"
    />
  );
}
